/**
 * The two ways an owner stops being the owner: hand the business on, or end it.
 *
 * Both are owner-only and both are typed, not clicked. The server decides —
 * who may transfer, to whom, whether an export exists — and these screens show
 * its answer rather than re-deriving it.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MEMBER_ROLE_LABELS, type Member } from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { Button, Card, CardHeader, Input, Modal, Select } from '../../components/ui';

/** Active, accepted members other than you — the only people ownership can go to. */
export function eligibleNewOwners(members: Member[], myUserId: string | undefined): Member[] {
  return members.filter(
    (m) => m.status === 'active' && m.userId !== null && m.userId !== myUserId && m.role !== 'owner',
  );
}

const label = (m: Member): string => `${m.fullName ?? m.email} — ${MEMBER_ROLE_LABELS[m.role]}`;

export function TransferOwnership({ members }: { members: Member[] }): ReactNode {
  const { business, user, reload } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();

  const eligible = eligibleNewOwners(members, user?.id);
  const [chosenId, setChosenId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const chosen = eligible.find((m) => m.id === chosenId) ?? null;
  const name = business?.businessName ?? '';
  const confirmed = typed.trim() === 'TRANSFER' || (name !== '' && typed.trim() === name);

  const transfer = useMutation({
    mutationFn: () =>
      api.post(path(`/members/${chosenId}/transfer-ownership`), { confirmation: typed.trim() }),
    onSuccess: async () => {
      setDone(chosen ? (chosen.fullName ?? chosen.email) : 'the new owner');
      setConfirming(false);
      // Your role just changed; everything that reads it must re-read it.
      await reload();
      void queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not transfer ownership.'),
  });

  if (business?.role !== 'owner' && !done) return null;

  return (
    <Card className="mt-5 max-w-3xl">
      <div id="transfer">
        <CardHeader
          title="Transfer ownership"
          subtitle="Make an existing member the owner of this business. You become an Admin."
        />
      </div>

      {done ? (
        <p role="status" className="text-sm text-slate-700">
          Ownership transferred to {done}. You are now an Admin of {name}.
        </p>
      ) : eligible.length === 0 ? (
        <p className="text-sm text-slate-600">
          Nobody can take over yet. Invite the person first; once they accept, they appear here.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[16rem] flex-1">
            <Select
              label="New owner"
              value={chosenId}
              onChange={(e) => setChosenId(e.target.value)}
              placeholder="Choose a member…"
              options={eligible.map((m) => ({ value: m.id, label: label(m) }))}
            />
          </div>
          <Button
            variant="secondary"
            disabled={!chosen}
            onClick={() => {
              setTyped('');
              setError(null);
              setConfirming(true);
            }}
          >
            Transfer…
          </Button>
        </div>
      )}

      <Modal open={confirming} onClose={() => setConfirming(false)} title="Transfer ownership">
        <form
          className="space-y-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (confirmed) transfer.mutate();
          }}
        >
          <dl className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
            <dt className="text-slate-500">Business</dt>
            <dd className="font-medium text-slate-900">{name}</dd>
            <dt className="text-slate-500">Current owner</dt>
            <dd className="text-slate-900">{user?.fullName} (you)</dd>
            <dt className="text-slate-500">New owner</dt>
            <dd className="text-slate-900">{chosen ? (chosen.fullName ?? chosen.email) : ''}</dd>
          </dl>
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
            You will become an <strong>Admin</strong>. Only the new owner can transfer the business
            again, manage billing or delete it. Everything in the business stays exactly as it is.
          </p>
          <Input
            label={`Type TRANSFER or ${name} to confirm`}
            name="transfer-confirmation"
            autoComplete="off"
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              setError(null);
            }}
          />
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!confirmed} loading={transfer.isPending}>
              Transfer ownership
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}

/**
 * Permanently deleting the business.
 *
 * The server refuses without a prior export unless told explicitly; the page
 * offers the export first and makes skipping it a stated choice.
 */
export function DeleteBusiness(): ReactNode {
  const { business, reload } = useAuth();
  const path = useBusinessPath();
  const navigate = useNavigate();

  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [skipExport, setSkipExport] = useState(false);
  const [exported, setExported] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const name = business?.businessName ?? '';
  const ready = typed.trim() === name && reason.trim().length >= 3 && (exported || skipExport);

  const exportData = useMutation({
    mutationFn: () => api.post(path('/data/export'), {}),
    onSuccess: () => setExported(true),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not export the data.'),
  });

  const erase = useMutation({
    mutationFn: () =>
      api.post(path('/data/erase'), {
        confirmation: typed.trim(),
        reason: reason.trim(),
        ...(exported ? {} : { acknowledgeNoExport: true }),
      }),
    onSuccess: async () => {
      await reload();
      navigate('/business', { replace: true });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not delete the business.'),
  });

  if (business?.role !== 'owner') return null;

  return (
    <Card className="mt-5 max-w-3xl border-red-200">
      <div id="delete">
        <CardHeader
          title="Delete this business"
          subtitle="Permanently removes the business and every record in it. This cannot be undone."
        />
      </div>
      <form
        className="space-y-4"
        noValidate
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (ready) erase.mutate();
        }}
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => exportData.mutate()}
            loading={exportData.isPending}
            disabled={exported}
          >
            {exported ? 'Export taken' : 'Export all data first'}
          </Button>
          {!exported && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={skipExport}
                onChange={(e) => setSkipExport(e.target.checked)}
              />
              Delete without an export
            </label>
          )}
        </div>
        <p className="text-xs text-slate-500">
          The export is a JSON file of every record in this business. Attachments are listed by
          name only — the files themselves (receipt photos, delivery proof) are not included.
          Credentials are never exported.
        </p>
        <Input
          label="Why are you deleting it?"
          name="erase-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <Input
          label={`Type ${name} to confirm`}
          name="erase-confirmation"
          autoComplete="off"
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            setError(null);
          }}
        />
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <Button type="submit" variant="danger" disabled={!ready} loading={erase.isPending}>
          Delete business permanently
        </Button>
      </form>
    </Card>
  );
}
